import React, { createContext, useEffect, useContext, useCallback, useMemo, useState } from 'react';
import authFetch from '../utils/authFetch';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import logger from '../utils/logger';

const AuthContext = createContext(null);

export const userQueryKey = ['currentUserProfile'];
const selectedTenantStorageKey = 'selectedTenant';
const HOPKINS_TENANT_ID = process.env.REACT_APP_BRANDING_TENANT_ID || "d07399fa-2ce1-4096-af6a-35a469a41387";

const getTenantFromStorage = () => {
     try {
         const tenantInfo = localStorage.getItem(selectedTenantStorageKey);
         return tenantInfo ? JSON.parse(tenantInfo) : null;
     } catch (e) {
         logger.error("Error parsing selectedTenant from localStorage", e);
         localStorage.removeItem(selectedTenantStorageKey);
         return null;
     }
};

const API_USERS_ME_PROFILE = '/users/me/profile';

const fetchUserProfile = async () => {
    logger.debug("AuthContext: fetchUserProfile triggered");
    const token = localStorage.getItem('accessToken');
    if (!token) {
        logger.warn("AuthContext: No token found, cannot fetch user profile.");
        throw new Error("Not authenticated");
    }

    try {
        const userProfileData = await authFetch(API_USERS_ME_PROFILE);
        logger.debug("AuthContext: User profile data received from /api/users/me/profile:", userProfileData);

        if (!userProfileData || !userProfileData.id) {
            logger.error("AuthContext: Invalid user profile data received from server.");
            throw new Error("Invalid user profile data received from server.");
        }
        return userProfileData;

    } catch (error) {
        logger.error("AuthContext: Error fetching user profile data:", error);
        if (error.status === 401 || error.message === "Authentication failed" || error.message === "Not authenticated") {
             throw new Error("Authentication failed");
        }
        if (error.status === 403) {
             logger.error("AuthContext: Forbidden access fetching profile (Tenant or User inactive/invalid context?). Treating as auth failure.");
             throw new Error("Authentication failed");
        }
        throw error;
    }
};

const getBrandingWithLocalOverride = (tenantAPIData) => {
    let logoUrlToUse = tenantAPIData?.logo_url || null;
    if (tenantAPIData?.id === HOPKINS_TENANT_ID) {
        logoUrlToUse = "/images/hopkinslogoHighRes.jpg";
    }
    return {
        logoUrl: logoUrlToUse,
        primaryColor: tenantAPIData?.primary_theme_color || null,
        secondaryColor: tenantAPIData?.secondary_theme_color || null,
    };
};

export const AuthProvider = ({ children }) => {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const [smartClient, setSmartClient] = useState(null);
    const {
        data: user,
        status: userStatus, 
        error: userError,
        isFetching,
        refetch,
    } = useQuery({
        queryKey: userQueryKey,
        queryFn: fetchUserProfile,
        enabled: !!localStorage.getItem('accessToken'), 
        staleTime: 1000 * 60 * 5,  
        gcTime: 1000 * 60 * 30,  
        retry: (failureCount, error) => {
            if (error.message === "Authentication failed") {
                return false;
            }
            return failureCount < 1;
        },
    });
    const [selectedTenant, setSelectedTenantInternal] = useState(() => getTenantFromStorage());
    const isAuthenticated = userStatus === 'success' && !!user;
    const isLoading = isFetching && !user;
    const allActiveMemberships = useMemo(() => {
        if (!isAuthenticated || !user?.memberships) return [];
        return user.memberships.filter(m => m && m?.status?.toLowerCase() === 'active');
    }, [isAuthenticated, user]);
    const tenantBranding = useMemo(() => {
        if (!selectedTenant?.tenant) return { logoUrl: null, primaryColor: null, secondaryColor: null };
        return getBrandingWithLocalOverride(selectedTenant.tenant);
    }, [selectedTenant]);

    const updateSelectedTenant = useCallback((tenantInfo) => {
        setSelectedTenantInternal(tenantInfo);
        try {
            if (tenantInfo) {
                localStorage.setItem(selectedTenantStorageKey, JSON.stringify(tenantInfo));
            } else {
                localStorage.removeItem(selectedTenantStorageKey);
            }
        } catch (e) {
            logger.error("Failed to update selectedTenant in localStorage", e);
        }
    }, []);

    const logout = useCallback(() => {
        logger.info("AuthContext: Logging out.");
        const token = localStorage.getItem('accessToken');
        const tenant = getTenantFromStorage();

        if (token && tenant?.tenant_id) {
            authFetch('/api/auth/logout', { method: 'POST' }, tenant.tenant_id)
                .catch(err => logger.error("Backend logout notification failed:", err));
        }

        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        updateSelectedTenant(null);

        // This is the correct way to clear all data with React Query
        queryClient.clear();
        navigate('/login', { replace: true });

    }, [queryClient, navigate, updateSelectedTenant]);

    const login = useCallback((loginResponseData) => {
        const { access_token, user_profile } = loginResponseData || {};
        if (!access_token || !user_profile || !user_profile.memberships) {
            logger.error("Login response missing token, user_profile, or memberships within the profile.", loginResponseData);
            return;
        }
        queryClient.cancelQueries({ queryKey: userQueryKey });
        localStorage.setItem('accessToken', access_token);
        queryClient.setQueryData(userQueryKey, user_profile);
        const activeMships = user_profile.memberships.filter(m => m?.status?.toLowerCase() === 'active');
        const initialTenant = activeMships.find(m => m.is_default) || activeMships[0] || null;
        updateSelectedTenant(initialTenant);
        
        return true;

    }, [queryClient, updateSelectedTenant]);

    const selectTenant = useCallback((tenantInfo) => {
        if (selectedTenant?.tenant_id !== tenantInfo?.tenant_id) {
            logger.info(`AuthContext: Changing tenant to ${tenantInfo.tenant_name}`);
            updateSelectedTenant(tenantInfo);
        }
    }, [selectedTenant, queryClient, updateSelectedTenant]);
    useEffect(() => {
        if (userStatus === 'error' && userError?.message === "Authentication failed") {
            logger.warn("Authentication error detected in user profile query, forcing logout.");
            logout();
        }
    }, [userStatus, userError, logout]);
    useEffect(() => {
        if (isAuthenticated && !selectedTenant && allActiveMemberships.length > 0) {

            logger.info("No tenant selected, auto-selecting default or first available.");
            const defaultTenant = allActiveMemberships.find(m => m.is_default) || allActiveMemberships[0];
            updateSelectedTenant(defaultTenant);
        }
    }, [isAuthenticated, selectedTenant, allActiveMemberships, updateSelectedTenant]);

    const authContextValue = useMemo(() => ({
        user,
        login,
        logout,
        isLoading,
        isAuthenticated,
        isFetchingUser: isFetching,
        isUserError: userStatus === 'error',
        userError,
        refetchUserProfile: refetch,
        memberships: allActiveMemberships,
        selectedTenant: selectedTenant,
        activeMembership: selectedTenant, // Alias for consistency
        selectTenant,
        queryStatus: userStatus,
        tenantBranding,
        smartClient,
        setSmartClient,
    }), [
        user, login, logout, isLoading, isAuthenticated, isFetching,
        userStatus, userError, refetch, allActiveMemberships,
        selectedTenant, selectTenant, tenantBranding, smartClient,
    ]);

    return (
        <AuthContext.Provider value={authContextValue}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === null) {
         logger.error("useAuth must be used within an AuthProvider!");
         const dummyLogin = () => Promise.resolve(false);
         const dummyLogout = () => {};
         const dummySelectTenant = () => {};
         const dummyRefetch = () => Promise.resolve();
         return {
             isLoading: true, isAuthenticated: false, user: null,
             login: dummyLogin, logout: dummyLogout, memberships: [],
             selectedTenant: null, activeMembership: null,
             selectTenant: dummySelectTenant,
             isFetchingUser: true, isUserError: false, userError: null,
             refetchUserProfile: dummyRefetch, queryStatus: 'pending',
             tenantBranding: { logoUrl: null, primaryColor: null, secondaryColor: null },
            };
    }
    return context;
};

// ADDED: refreshToken function to handle session expiry
export const refreshToken = async () => {
  const currentRefreshToken = localStorage.getItem('refreshToken');
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '';

  if (!currentRefreshToken) {
    console.error('No refresh token available.');
    return null;
  }

  try {
    const response = await fetch(`${apiBaseUrl}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: currentRefreshToken }),
    });

    if (!response.ok) {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      throw new Error('Failed to refresh token');
    }

    const data = await response.json();
    localStorage.setItem('accessToken', data.access_token);
    if (data.refresh_token) {
        localStorage.setItem('refreshToken', data.refresh_token);
    }
    return data.access_token;
  } catch (error) {
    console.error('An error occurred during token refresh:', error);
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    return null;
  }
};